import { t } from 'translations'

export type TemplateType =
    | 'cart_pay_later'
    | 'cart_pay_bank_transfer'
    | 'cart_paid'
    | 'cart_paid_apf'
    | 'cart_reminder'
    | 'cart_reminder_2'
    | 'rtp_cart_reminder'
    | 'pre_event'
    | 'post_event'
    | 'reservation'
    | 'reservation_apf'
    | 'refund'
    | 'order_date_change'

export type FormItemType =
    | 'defaultLanguage'
    | 'activeLanguage'
    | 'title'
    | 'paragraphText'
    | 'textline'
    | 'footerText'
    | 'ctaUrl'
    | 'discountReservoir'
    | 'themeColor'
    | 'backgroundColor'
    | 'signatureEnabled'
    | 'socialIconsEnabled'
    | 'logo'
    | 'agentPhoto'
    | 'subject'
    | 'preheader'
    | 'vatEnabled'
    | 'upsells'
    | 'upsellsEnabled'
    | 'requiresOptIn'

export type TemplateStatusType = 'enabled' | 'disabled'
export type MenuStepType = 'settings' | 'text'

export const emailNameMap = {
    get CART_PAID() {
        return t('emailTemplates.names.cartPaid')
    },
    get CART_PAID_INPUT_REQUIRED() {
        return t('emailTemplates.names.cartPaidInputRequired')
    },
    get CART_PAY_LATER() {
        return t('emailTemplates.names.cartPayLater')
    },
    get CART_PAY_BANK_TRANSFER() {
        return t('emailTemplates.names.cartPayBankTransfer')
    },
    get CART_RESERVATION() {
        return t('emailTemplates.names.cartReservation')
    },
    get CART_RESERVATION_INPUT_REQUIRED() {
        return t('emailTemplates.names.cartReservationInputRequired')
    },
    get CART_REMINDER() {
        return t('emailTemplates.names.cartReminder')
    },
    get CART_REMINDER_II() {
        return t('emailTemplates.names.cartReminderII')
    },
    get CART_REMINDER_RTP() {
        return t('emailTemplates.names.cartReminderRtp')
    },
    get CART_PAID_INPUT_REQUIRED_REMINDER() {
        return t('emailTemplates.names.cartPaidInputRequiredReminder')
    },
    get CART_REFUND() {
        return t('emailTemplates.names.cartRefund')
    },
    get CART_PRE_EVEN_NOTIFICATION() {
        return t('emailTemplates.names.cartPreEventNotification')
    },
    get CART_POST_EVENT_NOTIFICATION() {
        return t('emailTemplates.names.cartPostEventNotification')
    },
    get ORDER_DATE_CHANGE() {
        return t('emailTemplates.names.orderDateChange')
    },
} as const

export const cartPaidTemplates = ['cart_paid', 'cart_paid_apf', 'reservation', 'reservation_apf', 'order_date_change']
export const reminderTemplates = ['cart_reminder', 'cart_reminder_2', 'rtp_cart_reminder']

export const payLaterTemplates = ['cart_pay_later', 'cart_pay_bank_transfer']

export interface Template {
    type: TemplateType
    status: TemplateStatusType
    custom: boolean
}

export interface Copy {
    [languageCode: string]: string
}

export interface TemplateSettings {
    subject: Copy
    preheader: Copy
    title: Copy
    paragraphText: Copy
    textline: Copy
    footerText: Copy
    ctaUrl: Copy
    themeColor: string
    backgroundColor: string
    signatureEnabled: boolean
    socialIconsEnabled: boolean
    upsellsEnabled: boolean
    vatEnabled: boolean
    requiresOptIn: boolean | null
    logo: string | null
    agentPhoto: string | null
    discountReservoir: string | null
}

export interface TemplateSettingsWithVariables extends TemplateSettings {
    variables: string[]
}

interface PayloadUpsell {
    uuid: string
    priority: number
    image: string
}

export interface PayloadTemplateSettings extends TemplateSettings {
    upsells?: PayloadUpsell[]
}

interface TemplateConfig {
    [type: string]: {
        name: string
        description: string
        customFields: FormItemType[]
    }
}

export const globalFieldList: FormItemType[] = [
    'subject',
    'preheader',
    'title',
    'themeColor',
    'backgroundColor',
    'signatureEnabled',
    'socialIconsEnabled',
    'logo',
    'agentPhoto',
]
export const optionalFieldList: FormItemType[] = [
    'paragraphText',
    'textline',
    'footerText',
    'ctaUrl',
    'discountReservoir',
    'upsells',
    'requiresOptIn',
]
export const translationFieldList: FormItemType[] = [
    'subject',
    'preheader',
    'title',
    'paragraphText',
    'textline',
    'footerText',
    'ctaUrl',
]

export const templatesConfig: TemplateConfig = {
    cart_paid: {
        get name() {
            return emailNameMap['CART_PAID']
        },
        get description() {
            return t('emailTemplates.descriptions.cartPaid')
        },
        customFields: ['paragraphText', 'textline', 'footerText', 'vatEnabled'],
    },
    cart_pay_later: {
        get name() {
            return emailNameMap['CART_PAY_LATER']
        },
        get description() {
            return t('emailTemplates.descriptions.cartPayLater')
        },
        customFields: ['paragraphText'],
    },
    cart_pay_bank_transfer: {
        get name() {
            return emailNameMap['CART_PAY_BANK_TRANSFER']
        },
        get description() {
            return t('emailTemplates.descriptions.cartPayBankTransfer')
        },
        customFields: ['paragraphText', 'textline'],
    },
    cart_paid_apf: {
        get name() {
            return emailNameMap['CART_PAID_INPUT_REQUIRED']
        },
        get description() {
            return t('emailTemplates.descriptions.cartPaidApf')
        },
        customFields: ['paragraphText', 'textline', 'footerText', 'vatEnabled'],
    },
    reservation: {
        get name() {
            return emailNameMap['CART_RESERVATION']
        },
        get description() {
            return t('emailTemplates.descriptions.reservation')
        },
        customFields: ['paragraphText', 'textline', 'footerText', 'vatEnabled'],
    },
    reservation_apf: {
        get name() {
            return emailNameMap['CART_RESERVATION_INPUT_REQUIRED']
        },
        get description() {
            return t('emailTemplates.descriptions.reservationApf')
        },
        customFields: ['paragraphText', 'textline', 'footerText', 'vatEnabled'],
    },
    order_date_change: {
        get name() {
            return emailNameMap['ORDER_DATE_CHANGE']
        },
        get description() {
            return t('emailTemplates.descriptions.orderDateChange')
        },
        customFields: ['paragraphText', 'textline', 'footerText', 'vatEnabled'],
    },
    refund: {
        get name() {
            return emailNameMap['CART_REFUND']
        },
        get description() {
            return t('emailTemplates.descriptions.refund')
        },
        customFields: ['paragraphText', 'footerText'],
    },
    cart_reminder: {
        get name() {
            return emailNameMap['CART_REMINDER']
        },
        get description() {
            return t('emailTemplates.descriptions.cartReminder')
        },
        customFields: ['textline', 'requiresOptIn'],
    },
    cart_reminder_2: {
        get name() {
            return emailNameMap['CART_REMINDER_II']
        },
        get description() {
            return t('emailTemplates.descriptions.cartReminder2')
        },
        customFields: ['textline', 'requiresOptIn'],
    },
    rtp_cart_reminder: {
        get name() {
            return emailNameMap['CART_REMINDER_RTP']
        },
        get description() {
            return t('emailTemplates.descriptions.rtpCartReminder')
        },
        customFields: ['paragraphText', 'textline', 'requiresOptIn'],
    },
    pre_event: {
        get name() {
            return emailNameMap['CART_PRE_EVEN_NOTIFICATION']
        },
        get description() {
            return t('emailTemplates.descriptions.preEvent')
        },
        customFields: ['paragraphText', 'footerText', 'upsellsEnabled', 'requiresOptIn'],
    },
    post_event: {
        get name() {
            return emailNameMap['CART_POST_EVENT_NOTIFICATION']
        },
        get description() {
            return t('emailTemplates.descriptions.postEvent')
        },
        customFields: ['paragraphText', 'textline', 'ctaUrl', 'discountReservoir', 'requiresOptIn'],
    },
}

export const dummySettings: TemplateSettingsWithVariables = {
    variables: [],
    subject: { en: '' },
    preheader: { en: '' },
    title: { en: '' },
    paragraphText: { en: '' },
    textline: { en: '' },
    footerText: { en: '' },
    ctaUrl: { en: '' },
    themeColor: '#5298F4',
    backgroundColor: '#F0F0F0',
    signatureEnabled: true,
    socialIconsEnabled: true,
    vatEnabled: false,
    upsellsEnabled: false,
    requiresOptIn: false,
    logo: '',
    agentPhoto: '',
    discountReservoir: '',
}
